import { adminApi } from '@/features/admin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertCircle, Calendar, Camera, Heart, TrendingUp, Users } from 'lucide-react';
import type { ReactNode } from 'react';

function StatTile({
  label,
  value,
  icon,
  loading,
}: {
  label: string;
  value: number | undefined;
  icon: ReactNode;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-bold tabular-nums">
            {(value ?? 0).toLocaleString('fr-FR')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Bar({
  label,
  value,
  total,
  className,
}: { label: string; value: number; total: number; className: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {value.toLocaleString('fr-FR')} · {pct}%
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${className}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ErrorBox({ what }: { what: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-sm text-destructive">
      <AlertCircle className="h-4 w-4" />
      Impossible de charger {what}.
    </div>
  );
}

/** One entry per day over the last 30 days (days without signups at 0). */
function fillDays(daily: { date: string; count: number }[]) {
  const byDate = new Map(daily.map((d) => [d.date, d.count]));
  return Array.from({ length: 30 }, (_, i) => {
    const date = format(subDays(new Date(), 29 - i), 'yyyy-MM-dd');
    return { date, count: byDate.get(date) ?? 0 };
  });
}

export default function AdminStatsPage() {
  const statsQuery = useQuery({ queryKey: ['admin-stats'], queryFn: adminApi.getStats });
  const regQuery = useQuery({
    queryKey: ['admin-registration-stats'],
    queryFn: adminApi.getRegistrationStats,
  });
  const stats = statsQuery.data;
  const reg = regQuery.data;
  const genderTotal = (stats?.genderStats?.male ?? 0) + (stats?.genderStats?.female ?? 0);
  const photoTotal = (stats?.photoStats?.verified ?? 0) + (stats?.photoStats?.unverified ?? 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold sm:text-3xl">Statistiques</h1>

      {statsQuery.isError && (
        <Card>
          <CardContent>
            <ErrorBox what="les statistiques" />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatTile
          label="Total utilisateurs"
          value={stats?.totalUsers}
          loading={statsQuery.isLoading}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        <StatTile
          label="Inscrits aujourd'hui"
          value={stats?.newUsersToday}
          loading={statsQuery.isLoading}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        />
        <StatTile
          label="Inscrits cette semaine"
          value={stats?.newUsersThisWeek}
          loading={statsQuery.isLoading}
          icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
        />
        <StatTile
          label="Actifs (7 jours)"
          value={stats?.activeUsers}
          loading={statsQuery.isLoading}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        <StatTile
          label="Photos à valider"
          value={stats?.pendingPhotos}
          loading={statsQuery.isLoading}
          icon={<Camera className="h-4 w-4 text-muted-foreground" />}
        />
        <StatTile
          label="Rendez-vous en attente"
          value={stats?.pendingMeetings}
          loading={statsQuery.isLoading}
          icon={<Heart className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Inscriptions bloquées</CardTitle>
            <CardDescription>
              Comptes en attente créés ces 100 derniers jours, par étape manquante
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {regQuery.isLoading ? (
              [...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
            ) : regQuery.isError ? (
              <ErrorBox what="l'entonnoir d'inscription" />
            ) : (
              reg?.steps.map((step) => (
                <Bar
                  key={step.step}
                  label={step.label ?? `Étape ${step.step}`}
                  value={step.count}
                  total={reg.steps.reduce((sum, s) => sum + s.count, 0)}
                  className="bg-primary"
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Répartition</CardTitle>
            <CardDescription>Genre des membres et état des photos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {statsQuery.isLoading ? (
              [...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
            ) : (
              <>
                <Bar
                  label="Hommes"
                  value={stats?.genderStats?.male ?? 0}
                  total={genderTotal}
                  className="bg-blue-500"
                />
                <Bar
                  label="Femmes"
                  value={stats?.genderStats?.female ?? 0}
                  total={genderTotal}
                  className="bg-pink-500"
                />
                <Bar
                  label="Photos validées"
                  value={stats?.photoStats?.verified ?? 0}
                  total={photoTotal}
                  className="bg-success"
                />
                <Bar
                  label="Photos non validées"
                  value={stats?.photoStats?.unverified ?? 0}
                  total={photoTotal}
                  className="bg-destructive"
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inscriptions par jour</CardTitle>
          <CardDescription>30 derniers jours</CardDescription>
        </CardHeader>
        <CardContent>
          {regQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : regQuery.isError ? (
            <ErrorBox what="les inscriptions par jour" />
          ) : reg?.daily.some((d) => d.count > 0) ? (
            <div className="overflow-x-auto">
              <div className="flex h-44 min-w-[480px] items-end gap-1">
                {fillDays(reg.daily).map((day, i) => (
                  <div
                    key={day.date}
                    className="flex flex-1 flex-col items-center justify-end"
                    title={`${format(parseISO(day.date), 'd MMM', { locale: fr })} : ${day.count}`}
                  >
                    <span className="mb-1 text-[10px] tabular-nums text-muted-foreground">
                      {day.count > 0 ? day.count : ''}
                    </span>
                    <div
                      className="w-full rounded-t bg-primary"
                      style={{
                        height: `${reg.maxDaily > 0 && day.count > 0 ? Math.max(4, (day.count / reg.maxDaily) * 128) : 0}px`,
                      }}
                    />
                    <span className="mt-1 h-3 text-[10px] text-muted-foreground">
                      {i % 5 === 4 ? format(parseISO(day.date), 'd/MM') : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aucune inscription sur la période
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
